import type { IntentSuggestionDto } from './schema';

export type GhostCompletion = {
  suggestion: IntentSuggestionDto;
  /** Visible grey suffix after the visitor's current input. */
  ghostSuffix: string;
  /** Index into the ordered match list (for ArrowUp/Down cycling). */
  matchIndex: number;
  matchCount: number;
};

function prefixMatches(partial: string, insertText: string): boolean {
  if (!partial) return false;
  if (insertText.length <= partial.length) return false;
  return insertText.toLowerCase().startsWith(partial.toLowerCase());
}

/**
 * Suggestions whose `insert_text` is a case-insensitive prefix completion of `partial`.
 * Order preserved from the API ranking (caller already ranked).
 */
export function listGhostMatches(
  partial: string,
  suggestions: readonly IntentSuggestionDto[],
): IntentSuggestionDto[] {
  const trimmed = partial;
  if (!trimmed.trim()) return [];
  return suggestions.filter((s) => prefixMatches(trimmed, s.insertText));
}

/**
 * Cursor-style ghost: grey suffix for the active match.
 * `cycleIndex` wraps within matches (ArrowDown / ArrowUp).
 */
export function resolveGhostCompletion(
  partial: string,
  suggestions: readonly IntentSuggestionDto[],
  cycleIndex = 0,
): GhostCompletion | null {
  const matches = listGhostMatches(partial, suggestions);
  if (matches.length === 0) return null;

  const n = matches.length;
  const idx = ((cycleIndex % n) + n) % n;
  const suggestion = matches[idx]!;
  const ghostSuffix = suggestion.insertText.slice(partial.length);

  if (!ghostSuffix) return null;

  return {
    suggestion,
    ghostSuffix,
    matchIndex: idx,
    matchCount: n,
  };
}
